import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../data/products';
import { useProductsStore } from '../store/productsStore';
import ProductCard from '../components/ProductCard';
import RecentlyViewed from '../components/RecentlyViewed';
import { categoryIconMap } from '../components/CarIcons';

function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const products = useProductsStore((state) => state.products);
  const categories = useProductsStore((state) => state.categories);
  const featured = useMemo(() => products.filter(p => p.isFeatured).slice(0, 4), [products]);
  const campaign = useMemo(() => products.filter(p => p.isCampaign).slice(0, 4), [products]);
  const newProducts = useMemo(() => products.filter(p => p.isNew).slice(0, 4), [products]);

  // Random 20 products for "Aracınız İçin En İyisi" - changes on each refresh
  const forYourCar = useMemo(() => {
    const inStock = products.filter(p => p.inStock);
    return shuffleArray(inStock).slice(0, 20);
  }, [products]);

  // Get most reviewed products as "most favorited"
  const mostFavorited = useMemo(() => [...products]
    .filter(p => p.inStock && p.reviewCount > 0)
    .sort((a, b) => b.reviewCount - a.reviewCount)
    .slice(0, 4), [products]);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0A0A0A] via-[#1a1a2e] to-[#16213e] text-white">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#E31E24]/10 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-[#FF4500]/10 rounded-full blur-3xl animate-pulse delay-1000" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-[#E31E24]/5 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-[#E31E24]/3 rounded-full" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-3xl">
              <div className="inline-block px-4 py-1.5 bg-[#E31E24]/20 border border-[#E31E24]/30 rounded-full text-[#E31E24] text-xs font-bold tracking-wider mb-6 uppercase">
                🏎️ T-RaX Car Care
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black leading-tight mb-6">
                <span className="bg-gradient-to-r from-white via-white to-gray-400 bg-clip-text text-transparent">
                  {t('home.hero')}
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-gray-400 mb-8 max-w-xl leading-relaxed">
                {t('home.heroSub')}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/magaza"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#E31E24] to-[#FF4500] text-white font-bold rounded-xl hover:shadow-2xl hover:shadow-[#E31E24]/40 transition-all duration-500 hover:-translate-y-0.5 uppercase tracking-wider text-sm"
                >
                  🛒 {t('home.shopNow')}
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            </div>
            {/* Realistic Drift Scene */}
            <div className="relative hidden lg:flex justify-center items-center" style={{ perspective: '1000px' }}>
              <div className="relative w-[500px] h-[500px]" style={{ transformStyle: 'preserve-3d' }}>
                
                {/* Background glow */}
                <div className="absolute inset-0 bg-[#E31E24]/8 blur-[120px] rounded-full" />
                
                {/* Track surface - asphalt */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[380px] h-[380px] rounded-full bg-gradient-to-br from-gray-900/80 to-gray-800/60 border border-white/5"
                  style={{ boxShadow: 'inset 0 0 60px rgba(0,0,0,0.5), 0 0 40px rgba(0,0,0,0.3)' }}
                />
                
                {/* Tire marks on track */}
                <svg className="absolute inset-0 w-full h-full" viewBox="0 0 500 500">
                  {/* Outer tire mark */}
                  <circle cx="250" cy="250" r="175" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="12" />
                  {/* Inner tire mark */}
                  <circle cx="250" cy="250" r="155" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="8" />
                  {/* Track boundary */}
                  <circle cx="250" cy="250" r="190" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" strokeDasharray="8 6" style={{ animation: 'track-dash 3s linear infinite' }} />
                  {/* Skid marks */}
                  <circle cx="250" cy="250" r="165" fill="none" stroke="rgba(227,30,36,0.15)" strokeWidth="4" strokeDasharray="30 20" style={{ animation: 'track-dash 2s linear infinite reverse' }} />
                </svg>

                {/* Center circle with T-RAX */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 rounded-full bg-gradient-to-br from-[#0A0A0A] to-[#111] border border-white/20 flex flex-col items-center justify-center z-30"
                  style={{ animation: 'center-glow 2s ease-in-out infinite' }}
                >
                  <span className="text-3xl font-black bg-gradient-to-r from-[#E31E24] to-[#FF4500] bg-clip-text text-transparent tracking-wider leading-none">
                    T-RAX
                  </span>
                  <span className="text-[9px] text-gray-500 font-semibold tracking-[0.2em] uppercase mt-1">Car Care</span>
                </div>

                {/* Main drifting car */}
                <div 
                  className="absolute top-1/2 left-1/2 z-20"
                  style={{ 
                    animation: 'drift-orbit 3.5s linear infinite',
                    marginLeft: '-80px',
                    marginTop: '-50px'
                  }}
                >
                  <div style={{ animation: 'car-drift-tilt 1.8s ease-in-out infinite' }}>
                    {/* Smoke behind car */}
                    <div className="absolute -left-8 top-1/2 -translate-y-1/2">
                      <div className="w-16 h-16 bg-white/20 rounded-full blur-xl"
                        style={{ animation: 'smoke-puff 0.6s ease-out infinite' }}
                      />
                      <div className="absolute left-2 top-2 w-12 h-12 bg-white/15 rounded-full blur-lg"
                        style={{ animation: 'smoke-puff 0.6s ease-out infinite 0.15s' }}
                      />
                      <div className="absolute left-4 top-4 w-8 h-8 bg-white/10 rounded-full blur-md"
                        style={{ animation: 'smoke-puff 0.6s ease-out infinite 0.3s' }}
                      />
                    </div>
                    
                    {/* The realistic drift car */}
                    <img
                      src="/drift-car.jpg"
                      alt="T-RaX Drift Car"
                      className="w-40 h-auto rounded-lg"
                      style={{ 
                        filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.8)) drop-shadow(0 0 20px rgba(227,30,36,0.3))',
                        transform: 'scaleX(-1)'
                      }}
                    />
                    
                    {/* Sparks from tires */}
                    <div className="absolute -bottom-2 right-4">
                      <div className="w-1.5 h-1.5 bg-[#FFD700] rounded-full"
                        style={{ animation: 'spark 0.3s ease-out infinite' }}
                      />
                      <div className="absolute left-3 top-1 w-1 h-1 bg-[#FF6B00] rounded-full"
                        style={{ animation: 'spark 0.3s ease-out infinite 0.1s' }}
                      />
                      <div className="absolute left-1 top-2 w-1 h-1 bg-[#FFD700] rounded-full"
                        style={{ animation: 'spark 0.3s ease-out infinite 0.2s' }}
                      />
                    </div>
                  </div>
                </div>

                {/* Second car (ghost - opposite direction) */}
                <div 
                  className="absolute top-1/2 left-1/2 z-10 opacity-25"
                  style={{ 
                    animation: 'drift-orbit 5s linear infinite reverse',
                    marginLeft: '-50px',
                    marginTop: '-30px'
                  }}
                >
                  <div style={{ animation: 'car-drift-tilt 2s ease-in-out infinite 0.5s' }}>
                    <img
                      src="/drift-car.jpg"
                      alt=""
                      aria-hidden="true"
                      className="w-28 h-auto"
                      style={{ 
                        filter: 'drop-shadow(0 0 15px rgba(255,69,0,0.3))',
                        transform: 'scaleX(-1) scaleY(0.9)'
                      }}
                    />
                  </div>
                </div>

                {/* Dynamic smoke particles around track */}
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-6 h-6 bg-white/10 rounded-full blur-lg"
                    style={{
                      top: `${50 + 35 * Math.sin((i * Math.PI * 2) / 6)}%`,
                      left: `${50 + 35 * Math.cos((i * Math.PI * 2) / 6)}%`,
                      animation: `smoke-puff 1s ease-out infinite ${i * 0.2}s`,
                    }}
                  />
                ))}

                {/* Speed lines */}
                {[...Array(12)].map((_, i) => (
                  <div
                    key={`line-${i}`}
                    className="absolute h-px bg-gradient-to-r from-transparent via-[#E31E24]/30 to-transparent"
                    style={{
                      top: '50%',
                      left: '50%',
                      width: '20px',
                      transformOrigin: '0 0',
                      transform: `rotate(${i * 30}deg) translateX(140px)`,
                      animation: `tire-glow 2s ease-in-out infinite ${i * 0.15}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Speed lines decoration */}
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#E31E24]/50 to-transparent" />
      </section>

      {/* Aracınız İçin En İyisi - Random 20 products */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900">🚗 {t('home.forYourCar')}</h2>
          <Link to="/magaza" className="text-sm font-bold text-[#E31E24] hover:underline">{t('home.viewAll')} →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
          {forYourCar.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-black text-gray-900 mb-2">🏎️ {t('home.categories')}</h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#E31E24] to-[#FF4500] mx-auto rounded-full" />
        </div>
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
          {categories.filter(c => c.id !== 'kendi-setini-olustur').map((cat) => {
            const IconComp = categoryIconMap[cat.icon];
            return (
              <Link
                key={cat.id}
                to={`/magaza?kategori=${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-4 sm:p-6 bg-white rounded-2xl border border-gray-100 hover:border-[#E31E24]/30 hover:shadow-xl hover:shadow-[#E31E24]/10 transition-all duration-500 hover:-translate-y-1"
              >
                <div
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
                  style={{ backgroundColor: `${cat.color}15`, color: cat.color }}
                >
                  {IconComp && <IconComp size={28} />}
                </div>
                <span className="text-xs sm:text-sm font-semibold text-gray-700 text-center group-hover:text-[#E31E24] transition-colors leading-tight">
                  {cat.name}
                </span>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Most Favorited */}
      {mostFavorited.length > 0 && (
        <section className="bg-gradient-to-b from-gray-50 to-white py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">❤️ {t('home.mostFavorited')}</h2>
              <Link to="/magaza" className="text-sm font-bold text-[#E31E24] hover:underline">{t('home.viewAll')} →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {mostFavorited.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Campaign Products */}
      {campaign.length > 0 && (
        <section className="py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">🔥 {t('home.campaign')}</h2>
              <Link to="/magaza? kampanya=true" className="text-sm font-bold text-[#E31E24] hover:underline">{t('home.viewAll')} →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {campaign.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* New Products */}
      {newProducts.length > 0 && (
        <section className="bg-gradient-to-b from-gray-50 to-white py-12 sm:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900">✨ {t('home.newProducts')}</h2>
              <Link to="/magaza" className="text-sm font-bold text-[#E31E24] hover:underline">{t('home.viewAll')} →</Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
              {newProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Banner */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="relative overflow-hidden bg-gradient-to-r from-[#E31E24] to-[#FF4500] rounded-3xl p-8 sm:p-12 text-white">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2" />
          <div className="relative max-w-xl">
            <h3 className="text-2xl sm:text-3xl font-black mb-4">{t('home.hero')}</h3>
            <p className="text-white/80 mb-6">{t('home.heroSub')}</p>
            <Link
              to="/magaza"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#E31E24] font-bold rounded-xl hover:bg-gray-100 transition-colors"
            >
              🛒 {t('home.shopNow')}
            </Link>
          </div>
        </div>
      </section>

      {/* Recently Viewed */}
      <RecentlyViewed />
    </div>
  );
};

export default HomePage;
